import { useMemo, useState } from 'react';
import Spinner from '../../Spinner';
import {
    useCreateNote,
    useDeleteNote,
    usePatientNotes,
    useUpdateNote
} from '../../../hooks/usePatientNotes';
import type { PatientNote } from '../../../services/patient/notes';
import { DeleteNoteConfirmModal } from './DeleteNoteConfirmModal';
import { filterNotesByQuery } from './filterNotes';
import { NoteModal, type NoteModalSaveData } from './NoteModal';
import { NotesHeader } from './NotesHeader';
import { NotesList } from './NotesList';

interface PatientNotesTabProps {
    patientId: string;
    /** Gates the notes fetch — only true while the Notes tab is the active one. */
    isActive: boolean;
}

/**
 * Full-CRUD notes tab for the doctor dashboard. Doctors only ever see and
 * create `visibility: 'external'` notes — enforced server-side — so there is
 * no severity picker or visibility toggle here.
 */
function PatientNotesTab({ patientId, isActive }: PatientNotesTabProps) {
    const { notes, isLoading, isError } = usePatientNotes(patientId, { enabled: isActive });
    const createNote = useCreateNote(patientId);
    const updateNote = useUpdateNote(patientId);
    const deleteNote = useDeleteNote(patientId);

    const [editingNote, setEditingNote] = useState<PatientNote | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [deletingNote, setDeletingNote] = useState<PatientNote | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    const filteredNotes = useMemo(
        () => filterNotesByQuery(notes, searchQuery),
        [notes, searchQuery]
    );

    const handleCreate = (data: NoteModalSaveData) => {
        createNote.mutate(data, { onSuccess: () => setIsCreating(false) });
    };

    const handleUpdate = (data: NoteModalSaveData) => {
        if (!editingNote) return;
        updateNote.mutate(
            { noteId: editingNote.id, data },
            { onSuccess: () => setEditingNote(null) }
        );
    };

    const handleDeleteConfirm = () => {
        if (!deletingNote) return;
        deleteNote.mutate(deletingNote.id, { onSuccess: () => setDeletingNote(null) });
    };

    const handleTogglePin = (note: PatientNote) => {
        updateNote.mutate({ noteId: note.id, data: { pinned: !note.pinned } });
    };

    return (
        <div className="flex-1 min-h-0 flex flex-col">
            <NotesHeader
                searchQuery={searchQuery}
                onSearchQueryChange={setSearchQuery}
                onAddNote={() => setIsCreating(true)}
            />

            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-5 py-4">
                {isError ? (
                    <div className="flex items-center justify-center h-full">
                        <p className="text-sm text-red-500">
                            Unable to load notes. Please try again.
                        </p>
                    </div>
                ) : isLoading ? (
                    <div className="flex items-center justify-center h-24">
                        <Spinner className="h-5 w-5 text-gray-400" />
                    </div>
                ) : (
                    <NotesList
                        notes={filteredNotes}
                        isLoading={false}
                        isSearching={searchQuery.trim().length > 0}
                        onEdit={setEditingNote}
                        onDelete={setDeletingNote}
                        onPin={handleTogglePin}
                    />
                )}
            </div>

            {isCreating && (
                <NoteModal
                    patientId={patientId}
                    note={null}
                    isSaving={createNote.isPending}
                    onSave={handleCreate}
                    onClose={() => setIsCreating(false)}
                />
            )}

            {editingNote && (
                <NoteModal
                    patientId={patientId}
                    note={editingNote}
                    isSaving={updateNote.isPending}
                    onSave={handleUpdate}
                    onClose={() => setEditingNote(null)}
                />
            )}

            {deletingNote && (
                <DeleteNoteConfirmModal
                    note={deletingNote}
                    isDeleting={deleteNote.isPending}
                    onConfirm={handleDeleteConfirm}
                    onCancel={() => setDeletingNote(null)}
                />
            )}
        </div>
    );
}

export { PatientNotesTab };
